import { GameState, GameMode, QuestionInstance, HistoryItem, Country, SubmissionResult } from '../types';
import { getGameData } from './dataLoader';
import { checkAnswer } from './templatesHelper';
import { APP_CONFIG } from '../constants';
import {
  buildFallbackQuestion,
  buildRecentQuestionKeys,
  generateQuestion,
} from './questionGenerator';
import {
  buildMultiQuestionSet,
  submitMultiQuestionAnswer,
} from './modes/multiQuestion';

// --- Initialization ---

export const initGame = (mode: GameMode): GameState => {
  if ([GameMode.FIVE_FOR_FIVE, GameMode.ROLLING_FIVE, GameMode.GRID_GAUNTLET].includes(mode)) {
    return buildMultiQuestionSet(mode);
  }

  const lives = mode === GameMode.SUDDEN_DEATH ? 1 : 
                mode === GameMode.STANDARD ? APP_CONFIG.MAX_LIVES_STANDARD : 
                Infinity; // Infinite modes have infinite lives
  const initialScore = mode === GameMode.QUESTION_SPRINT ? 1 : 0;
  const maxPossibleAnswers = mode === GameMode.QUESTION_SPRINT ? 50 : undefined;
  const questionFilters = { mode };

  const initialQuestion = generateQuestion(
    0,
    null,
    new Set(),
    new Set(),
    new Set(),
    [],
    { maxPossibleAnswers },
    questionFilters
  ) ?? buildFallbackQuestion(0, { maxPossibleAnswers }, new Set(), new Set(), undefined, questionFilters, []);

  return {
    mode,
    status: 'PLAYING',
    score: initialScore,
    lives,
    maxLives: lives,
    currentQuestion: initialQuestion,
    history: [],
    usedIsoCodes: new Set(),
    burnedIsoCodes: new Set(),
    startTime: Date.now(),
  };
};

// --- Action Handlers ---

export const submitAnswer = (
  state: GameState, 
  country: Country
): SubmissionResult => {
  if ([GameMode.FIVE_FOR_FIVE, GameMode.ROLLING_FIVE, GameMode.GRID_GAUNTLET].includes(state.mode)) {
    return submitMultiQuestionAnswer(state, country);
  }

  const data = getGameData();
  const { currentQuestion, usedIsoCodes, burnedIsoCodes } = state;

  // 1. Global Checks
  
  // Check if already used
  if (usedIsoCodes.has(country.iso3)) {
    return {
      valid: false,
      message: `${country.name} has already been used!`,
      newState: state,
    };
  }

  // Check if burned (Skip check for TIMED/SPRINT mode as wrong answers aren't stored/burned)
  if (![GameMode.INFINITE_TIMED, GameMode.QUESTION_SPRINT].includes(state.mode) && burnedIsoCodes.has(country.iso3)) {
    return {
      valid: false,
      message: `${country.name} was lost previously!`,
      newState: state,
    };
  }

  // 2. Question Logic Check
  const isValid = checkAnswer(currentQuestion.templateId, currentQuestion.subjectIso3, country, data);

  // 3. Handle Result

  if (isValid) {
    const newUsed = new Set(usedIsoCodes);
    newUsed.add(country.iso3);

    const newHistory: HistoryItem = {
      questionText: currentQuestion.text,
      templateId: currentQuestion.templateId,
      subjectIso3: currentQuestion.subjectIso3,
      answerIso3: country.iso3,
      isCorrect: true,
      timestamp: Date.now(),
    };

    // Check Win Condition for TIMED/SPRINT mode
    let newStatus = state.status;
    let endTime = state.endTime;
    if ([GameMode.INFINITE_TIMED, GameMode.QUESTION_SPRINT].includes(state.mode)) {
       // Check if all countries found
       if (newUsed.size === data.countries.length) {
         newStatus = 'GAME_OVER';
         endTime = Date.now();
       }
    }

    if (state.mode === GameMode.QUESTION_SPRINT) {
      return {
        valid: true,
        message: 'Correct!',
        newState: {
          ...state,
          status: newStatus,
          endTime: endTime,
          history: [newHistory, ...state.history],
          usedIsoCodes: newUsed,
        }
      };
    }

    let nextQuestion: QuestionInstance | null = null;
    if (newStatus !== 'GAME_OVER') {
      const recentQuestionKeys = buildRecentQuestionKeys(
        [newHistory, ...state.history],
        APP_CONFIG.QUESTION_COOLDOWN_COUNT
      );
      const maxPossibleAnswers = state.mode === GameMode.QUESTION_SPRINT ? 50 : undefined;
      const questionFilters = { mode: state.mode };
      nextQuestion = generateQuestion(
        state.score + 1,
        currentQuestion.category,
        newUsed,
        burnedIsoCodes,
        recentQuestionKeys,
        [newHistory, ...state.history],
        { maxPossibleAnswers },
        questionFilters
      );
    }

    if (!nextQuestion && newStatus !== 'GAME_OVER') {
      newStatus = 'GAME_OVER';
      endTime = Date.now();
    }

    return {
      valid: true,
      message: 'Correct!',
      newState: {
        ...state,
        score: state.score + 1,
        status: newStatus,
        endTime: endTime,
        history: [newHistory, ...state.history],
        usedIsoCodes: newUsed,
        // If game over, keep current question, else generate new
        currentQuestion: newStatus === 'GAME_OVER'
          ? state.currentQuestion
          : (nextQuestion ?? buildFallbackQuestion(
            state.score + 1,
            { maxPossibleAnswers },
            newUsed,
            burnedIsoCodes,
            undefined,
            { mode: state.mode },
            [newHistory, ...state.history]
          )),
      }
    };
  } else {
    // INVALID ANSWER
    
    // Mode Specific: Infinite Timed/Question Sprint - Do nothing, just return invalid, don't burn, don't store
    if ([GameMode.INFINITE_TIMED, GameMode.QUESTION_SPRINT].includes(state.mode)) {
      return {
        valid: false,
        message: 'Incorrect!',
        newState: state // No state change
      };
    }

    // Mode Specific: Infinite Score - Penalty (-1)
    let scorePenalty = 0;
    if (state.mode === GameMode.INFINITE_SCORE) {
      scorePenalty = 1;
    }

    // Manage Lives (only for Standard/Sudden Death)
    const newLives = state.lives - 1;
    const isGameOver = (state.mode === GameMode.SUDDEN_DEATH || state.mode === GameMode.STANDARD) && newLives <= 0;
    
    // Manage Burning
    // We burn in Infinite Score to prevent spamming the same wrong country
    const newBurned = new Set(burnedIsoCodes);
    newBurned.add(country.iso3);

    const newHistory: HistoryItem = {
      questionText: currentQuestion.text,
      templateId: currentQuestion.templateId,
      subjectIso3: currentQuestion.subjectIso3,
      answerIso3: country.iso3,
      isCorrect: false,
      timestamp: Date.now(),
    };

    return {
      valid: false,
      message: 'Incorrect!',
      burnedCountry: country,
      newState: {
        ...state,
        status: isGameOver ? 'GAME_OVER' : 'PLAYING',
        score: state.score - scorePenalty,
        lives: newLives,
        history: [newHistory, ...state.history],
        burnedIsoCodes: newBurned,
        endTime: isGameOver ? Date.now() : undefined
      }
    };
  }
};

export const advanceQuestion = (state: GameState): GameState => {
  if (state.status !== 'PLAYING') return state;
  const recentQuestionKeys = buildRecentQuestionKeys(
    state.history,
    APP_CONFIG.QUESTION_COOLDOWN_COUNT
  );
  const maxPossibleAnswers = state.mode === GameMode.QUESTION_SPRINT ? 50 : undefined;
  const nextQuestion = generateQuestion(
    state.score,
    state.currentQuestion.category,
    state.usedIsoCodes,
    state.burnedIsoCodes,
    recentQuestionKeys,
    state.history,
    { maxPossibleAnswers },
    { mode: state.mode }
  );

  if (!nextQuestion) {
    return {
      ...state,
      status: 'GAME_OVER',
      endTime: Date.now(),
    };
  }

  return {
    ...state,
    score: state.mode === GameMode.QUESTION_SPRINT ? state.score + 1 : state.score,
    currentQuestion: nextQuestion,
  };
};
